import {isSuperAction} from '../../../shared/weapon-motion';
import { readAppearance, type AtelierAppearance } from '../../../shared/atelier';
import type { AtelierAction, AtelierActorFrame, AtelierActorRuntime } from '../../../shared';
import { applyPose, blendPreviewPose } from '../anim';
import {MANUSCRIPT_MORPHS,manuscriptFace} from '../manuscript';
import { FaceAnimator, defaultShape, FACE_VEC4S, packFace } from '../face';
import { createBoundsProxy, posedBounds } from '../framing';
import { modelMatrix } from '../math';
import { boneTip, evaluate, qEuler, qMul, restPose, type Pose, type Quat } from '../pose';
import { assembleAtelierGeometry, type AtelierAssembly } from './assembly';
import { prepareAtelierModel, type PreparedAtelierModel } from './model';
import { resolveCoat } from '../palette';
import {ATELIER_MODEL_IDS,atelierModelUrl} from '../../../shared/atelier-species';
import {applyTerminalPose} from '../terminalMotion';
import {applyArmedMotion} from '../armedMotion';

/** Reflect only the authored action, never the source mesh or its UVs. */
function mirrorStrike(sk:PreparedAtelierModel['body']['sk'],pose:Pose):void {
  const reflect=([x,y,z,w]:Quat):Quat=>[x,-y,-z,w];
  for(const stem of ['upperArm','foreArm','hand','thigh','shin','foot']) {
    const l=sk.byName.get(stem+'L'),r=sk.byName.get(stem+'R');if(l===undefined||r===undefined)continue;
    const left=pose.bones[l].rot;pose.bones[l].rot=reflect(pose.bones[r].rot);pose.bones[r].rot=reflect(left);
  }
  for(const name of ['pelvis','chest','neck','head']){const i=sk.byName.get(name);if(i!==undefined)pose.bones[i].rot=reflect(pose.bones[i].rot);}
  pose.rootOffset[0]*=-1;
}
const MODEL_IDS:readonly string[]=ATELIER_MODEL_IDS;
interface ModelEntry {promise:Promise<PreparedAtelierModel>;refs:number}
const models=new Map<string,ModelEntry>();
const geometry=new Map<string,{assembly:AtelierAssembly;refs:number}>();
async function acquireModel(id:string):Promise<PreparedAtelierModel>{
  if(!MODEL_IDS.includes(id))throw new Error('Unknown atelier model: '+id);
  let entry=models.get(id);
  if(!entry){entry={refs:0,promise:prepareAtelierModel({id,url:atelierModelUrl(id)})};models.set(id,entry);}
  entry.refs++;
  try{return await entry.promise;}catch(error){entry.refs--;if(models.get(id)===entry)models.delete(id);throw error;}
}
function releaseModel(id:string):void{
  const entry=models.get(id);if(!entry)return;entry.refs--;
  // Only five authored bases exist. Retain at most three idle decoded textures.
  const idle=[...models].filter(([,value])=>value.refs===0);
  while(idle.length>3){const [key,value]=idle.shift()!;models.delete(key);void value.promise.then(model=>model.albedo.close(),()=>{});}
}

export async function atelierPreset(model:string,outfit:string,name:string):Promise<AtelierAppearance>{
  const source=await acquireModel(model);
  try{
    const appearance=readAppearance({version:1,model,head:'own',coat:'orig',outfit,name,morphs:{...MANUSCRIPT_MORPHS},face:manuscriptFace(defaultShape(model,source.fit)),emotion:'calm',dye:'original',...(model==='wolf-st'?{coatPattern:'saddle'}:model==='lynx-st'?{coatPattern:'rosettes'}:{})});
    if(!appearance)throw new Error('Invalid atelier NPC preset');return appearance;
  }finally{releaseModel(model);}
}

/** Independent mutable pose and face state around shareable immutable geometry. */
export function createPreparedAtelierActor(recipe:AtelierAppearance,source:PreparedAtelierModel,head:PreparedAtelierModel,assembly:AtelierAssembly,onDispose=()=>{}):AtelierActorRuntime {
  const appearance=readAppearance(recipe);if(!appearance)throw new Error('Invalid atelier appearance');
  const sk=source.body.sk,pose=restPose(sk),face=new FaceAnimator();face.emotion=appearance.emotion;
  const faceHost=new Float32Array(FACE_VEC4S*4),world=new Float32Array(16);
  let disposed=false,lastTime:number|null=null,action:AtelierAction='idle',transition:{from:Pose;start:number}|null=null;
  let heldGround:number|null=null;
  const weaponDraws=assembly.draws.filter(draw=>draw.material==='cloth'&&draw.id.startsWith('weapon-'));
  const weaponPositions=new Set(weaponDraws.map(draw=>draw.pos));
  const bodyFraming=assembly.framing.filter(mesh=>!weaponPositions.has(mesh.pos));
  const heldPropGround=new Map<number,number>();
  const frame:AtelierActorFrame={palette:new Float32Array(sk.bones.length*16),face:faceHost,model:world,coat:resolveCoat(appearance),coatPattern:appearance.coatPattern,coatSeed:appearance.coatSeed,coatScale:appearance.coatScale,time:0,bounds:{minY:0,maxY:1,radius:.5},hitFlash:0,anchors:{hands:[[0,0,0],[0,0,0]],chest:[0,0,0],neck:[0,0,0],head:[0,0,0],torsoRadius:source.body.lm.radius.torso,headRadius:assembly.contactHead.radius}};
  const evaluation=evaluate(sk,pose);
  frame.coatFit=[source.body.lm.chest[0],source.body.lm.chest[1],source.body.lm.radius.torso];
  const boundsProxy=[createBoundsProxy(assembly.framing)];
  let fitted=false;
  return {
    appearance,
    asset:{draws:assembly.draws,textures:{body:source.albedo,...(head!==source?{head:head.albedo}:{})}},
    sample(time,options={}){
      if(disposed)throw new Error('Atelier actor already disposed');
      const dt=lastTime===null?1/60:Math.max(0,Math.min(.05,time-lastTime));lastTime=time;
      const terminal=options.action==='victory'||options.action==='defeat';
      const next=options.reducedMotion&&!terminal?(options.action==='guard'?'guard':'idle'):options.action??'idle';
      if(next!==action){transition={from:structuredClone(pose),start:time};action=next;heldGround=null;heldPropGround.clear();}
      const local=options.actionTime??time;
      const motion=next==='walk'||next==='greet'||next==='strike'||next==='guard'||next==='prayer'||isSuperAction(next)?next:'idle';
      face.emotion=next==='defeat'?'sleepy':next==='hit'?'surprised':next==='guard'||next==='strike'||isSuperAction(next)?'angry':next==='victory'?'smug':appearance.emotion;
        const look=applyPose(sk,pose,terminal?0:motion==='idle'||motion==='prayer'?time:local,appearance.morphs,!options.reducedMotion||terminal,{nod:0,tilt:terminal?0:face.cur.headTilt,talking:false,action:motion,weapon:appearance.weapon,weaponHead:assembly.contactHead,oneShot:true});
      if(next==='victory'||next==='defeat'){
        const terminalTime=options.reducedMotion?1.4:local;
        applyTerminalPose(sk,pose,next,terminalTime,assembly.weaponGrips);
        if(next==='victory'&&appearance.weapon)applyArmedMotion(sk,pose,terminalTime,appearance.weapon,'victory',assembly.contactHead);
      }
      const rotate=(name:string,x:number,y:number,z:number)=>{const bone=sk.byName.get(name);if(bone!==undefined)pose.bones[bone].rot=qMul(pose.bones[bone].rot,qEuler(x,y,z));};
      if(!options.reducedMotion){
        if(next==='feint'){const f=Math.sin(Math.min(1,local/.7)*Math.PI);rotate('chest',0,-.3*f,-.06*f);rotate('upperArmR',-.5*f,0,-.15*f);rotate('head',0,.25*f,0);}
        if(next==='hit'){const recoil=Math.sin(Math.min(1,local/.42)*Math.PI);rotate('chest',-.16*recoil,.07*recoil,0);rotate('head',-.16*recoil,0,.12*recoil);}
        if(options.mirrorAction&&!appearance.weapon&&motion==='strike')mirrorStrike(sk,pose);
        if(transition){
          const w=Math.min(1,Math.max(0,(time-transition.start)/.11));
          // Terminal grounding is mesh-based below. At the exact endpoint,
          // avoid re-grounding the finished pose by its feet and caching that
          // extra correction when a saved result is loaded directly.
          if(w<1||!terminal)blendPreviewPose(sk,pose,transition.from,w*w*(3-2*w));
          if(w===1)transition=null;
        }
      }else transition=null;
      face.update(dt,look.yaw,look.pitch);evaluate(sk,pose,evaluation);
      if(terminal){
        const held=(options.reducedMotion||local>=1.4)&&!transition;
        const correction=held&&heldGround!==null?heldGround:.014-posedBounds(next==='defeat'?bodyFraming:assembly.framing,evaluation.skin).minY;
        if(held)heldGround=correction;
        pose.rootOffset[1]+=correction;evaluate(sk,pose,evaluation);
        if(next==='defeat')for(const grip of assembly.weaponGrips??[]){
          // The defeated hand lets go. The rigid prop settles independently
          // beside it, rather than suspending the corpse on a staff or axe.
          const meshes=weaponDraws.filter(draw=>draw.joints[0]===grip.bone);
          const drop=held&&heldPropGround.has(grip.bone)?heldPropGround.get(grip.bone)!:.012-posedBounds(meshes,evaluation.skin).minY;
          if(held)heldPropGround.set(grip.bone,drop);
          const f=options.reducedMotion?1:Math.max(0,Math.min(1,(local-.35)/.45));
          const amount=drop<0?drop*f*f*(3-2*f):drop;
          const parent=evaluation.world[sk.bones[grip.bone].parent];
          pose.bones[grip.bone].offset=[parent[1]*amount,parent[4]*amount,parent[7]*amount];
        }
        if(next==='defeat'&&assembly.weaponGrips?.length)evaluate(sk,pose,evaluation);
      }
      frame.palette.set(evaluation.skin);
      const uv=head.skinUv;packFace(faceHost,0,appearance.face,face.cur,face.gaze,[uv[0],uv[1]],[uv[2],uv[3]],true,false);faceHost[49]=time;
      modelMatrix(1,options.yaw??0,options.x??0,options.y??0,options.z??0,world);
      if(options.orientation){
        // Whole-actor reaction about the actual posed chest, applied to both
        // mesh matrix and all anchors. No limb/texture state is shared.
        const q=options.orientation,inv:Quat=[-q[0],-q[1],-q[2],q[3]];
        const rotatePoint=(p:number[])=>qMul(qMul(q,[p[0],p[1],p[2],0]),inv).slice(0,3);
        const ci=sk.byName.get('chest')!,a=evaluation.joints[ci],b=boneTip(sk,pose,evaluation,ci),p=a.map((v,i)=>(v+b[i])*.5);
        const pivot=[0,1,2].map(i=>world[i]*p[0]+world[4+i]*p[1]+world[8+i]*p[2]+world[12+i]);
        const offset=rotatePoint([0,1,2].map(i=>world[12+i]-pivot[i]));
        for(let c=0;c<3;c++){const basis=rotatePoint([world[c*4],world[c*4+1],world[c*4+2]]);for(let i=0;i<3;i++)world[c*4+i]=basis[i];}
        for(let i=0;i<3;i++)world[12+i]=pivot[i]+offset[i];
      }
      const worldPoint=(p:number[]):[number,number,number]=>[world[0]*p[0]+world[4]*p[1]+world[8]*p[2]+world[12],world[1]*p[0]+world[5]*p[1]+world[9]*p[2]+world[13],world[2]*p[0]+world[6]*p[1]+world[10]*p[2]+world[14]];
      frame.anchors.hands=['L','R'].map(side=>worldPoint(boneTip(sk,pose,evaluation,sk.byName.get('hand'+side)!))) as typeof frame.anchors.hands;
      const chest=sk.byName.get('chest')!,chestBase=evaluation.joints[chest],chestTop=boneTip(sk,pose,evaluation,chest);
      frame.anchors.chest=worldPoint(chestBase.map((v,i)=>(v+chestTop[i])*.5));
      frame.anchors.torsoRadius=source.body.lm.radius.torso*pose.bones[chest].thick;
      const attachment=(point:number[],name:string)=>{const o=sk.byName.get(name)!*16,m=evaluation.skin;return worldPoint([m[o]*point[0]+m[o+4]*point[1]+m[o+8]*point[2]+m[o+12],m[o+1]*point[0]+m[o+5]*point[1]+m[o+9]*point[2]+m[o+13],m[o+2]*point[0]+m[o+6]*point[1]+m[o+10]*point[2]+m[o+14]]);};
      frame.anchors.neck=attachment(assembly.contactHead.neck,'neck');
      frame.anchors.head=attachment(assembly.contactHead.center,'head');
      frame.anchors.headRadius=assembly.contactHead.radius*pose.bones[sk.byName.get('head')!].thick;
      frame.anchors.weaponTips=assembly.weaponGrips?.map(grip=>attachment(grip.tip,sk.bones[grip.bone].name));
        frame.anchors.weaponBases=assembly.weaponGrips?.map(grip=>attachment(grip.center,sk.bones[grip.palmBone??grip.bone].name));
      frame.groundContacts=next==='defeat'&&local>.45||next==='defeat'&&options.reducedMotion?
        [worldPoint(evaluation.joints[sk.byName.get('pelvis')!]),frame.anchors.chest,frame.anchors.head]
          .map((p,i)=>[p[0],p[2],.012,i===2?frame.anchors.headRadius*1.55:frame.anchors.torsoRadius*1.8]):undefined;
      frame.time=time;
      // First idle pose frames the recipe exactly. Motion uses a small conservative
      // proxy for contact/framing diagnostics, never every garment vertex each frame.
      frame.bounds=posedBounds(fitted?boundsProxy:assembly.framing,evaluation.skin);fitted=true;
      frame.hitFlash=options.reducedMotion?0:options.hitFlash??0;
      return frame;
    },
    dispose(){if(disposed)return;disposed=true;onDispose();},
  };
}

export async function createAtelierActor(recipe:AtelierAppearance):Promise<AtelierActorRuntime>{
  const appearance=readAppearance(recipe);if(!appearance)throw new Error('Invalid atelier appearance');
  const acquired:string[]=[];
  try{
    const source=await acquireModel(appearance.model);acquired.push(appearance.model);
    const head=appearance.head==='own'||appearance.head===appearance.model?source:await acquireModel(appearance.head);
    if(head!==source)acquired.push(appearance.head);
    const key=[appearance.model,head.modelId,appearance.outfit,appearance.morphs.ears,appearance.dye??'original',appearance.weapon??'',appearance.boots??'',appearance.charm??'',JSON.stringify(appearance.colors??{})].join('|');
    let entry=geometry.get(key);
    if(!entry){entry={refs:0,assembly:assembleAtelierGeometry({body:source.body,attributes:source.donor,head:head!==source?head.donor:undefined,outfit:appearance.outfit,earScale:appearance.morphs.ears,dye:appearance.dye,colors:appearance.colors,weapon:appearance.weapon,boots:appearance.boots,charm:appearance.charm})};geometry.set(key,entry);}
    entry.refs++;
    return createPreparedAtelierActor(appearance,source,head,entry.assembly,()=>{
      entry!.refs--;for(const id of acquired)releaseModel(id);
      for(const [old,value] of geometry){if(geometry.size<=12)break;if(!value.refs)geometry.delete(old);}
    });
  }catch(error){for(const id of acquired)releaseModel(id);throw error;}
}
